<template>
  <VaLayout
    :top="{ fixed: true, order: 2 }"
    :left="{ fixed: true, absolute: breakpoints.mdDown, order: 1, overlay: breakpoints.mdDown && !isSidebarMinimized }"
    @leftOverlayClick="isSidebarMinimized = true"
  >
    <template #top>
      <Header :is-mobile="isMobile" />
    </template>

    <template #left>
      <Sidebar :minimized="isSidebarMinimized" :animated="!isMobile" :mobile="isMobile" />
    </template>

    <template #content>
      <div :class="{ minimized: isSidebarMinimized }" class="app-layout__sidebar-wrapper">
        <div v-if="isFullScreenSidebar" class="flex justify-end">
          <VaButton class="px-4 py-4" icon="md_close" preset="plain" @click="onCloseSidebarButtonClick" />
        </div>
      </div>
      <Breadcrumbs v-if="!isMobile" />
      <main class="p-4 pt-0">
        <article>
          <RouterView />
        </article>
      </main>
    </template>
  </VaLayout>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { onBeforeRouteUpdate, useRouter } from 'vue-router'
import { useBreakpoint, useToast } from 'vuestic-ui'

import { useGlobalStore } from '../../stores/global-store'

import Header from '@/core/components/header'
import Sidebar from '@/core/components/sidebar'
import Breadcrumbs from '@/core/components/breadcrumbs'
import { useCompanyStore } from '@/features/companies'
import { authServices } from '@/features/auth'
import { useCurrentUser } from 'vuefire'

const breakpoints = useBreakpoint()
const user = useCurrentUser()
const { push } = useRouter()
const { init } = useToast()

const sidebarWidth = ref('16rem')
const sidebarMinimizedWidth = ref<undefined | string>(undefined)
const isMobile = ref(false)
const isTablet = ref(false)

const globalStore = useGlobalStore()
const companyStore = useCompanyStore()
const { isSidebarMinimized } = storeToRefs(globalStore)

const isFullScreenSidebar = computed(() => isTablet.value && !isSidebarMinimized.value)

const onCloseSidebarButtonClick = () => {
  isSidebarMinimized.value = true
}

function onResize() {
  isSidebarMinimized.value = breakpoints.mdDown
  isMobile.value = breakpoints.smDown
  isTablet.value = breakpoints.mdDown
  sidebarMinimizedWidth.value = isMobile.value ? '0' : '4.5rem'
  sidebarWidth.value = isTablet.value ? '100%' : '16rem'
}

async function verifyCurrentCompany() {
  if (!companyStore.currentCompany) {
    const { ok, message } = await companyStore.fetchCompanies(user.value!.uid)
    if (!ok) {
      await authServices.signOut()
      init({ message, color: 'warning' })
      push({ name: 'login' })
    }
  }
}

onMounted(async () => {
  window.addEventListener('resize', onResize)
  onResize()

  await verifyCurrentCompany()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})

onBeforeRouteUpdate(() => {
  if (breakpoints.mdDown) {
    // Collapse sidebar after route change for Mobile
    isSidebarMinimized.value = true
  }
})
</script>

<style lang="scss" scoped>
// Prevent icon jump on animation
.va-sidebar {
  width: unset !important;
  min-width: unset !important;
}
</style>
